import {
	Badge,
	Box,
	Button,
	Container,
	Overlay,
	Paper,
	Progress,
	SimpleGrid,
	Text,
	TextInput,
	Title,
	UnstyledButton,
	Image,
	ActionIcon,
} from '@mantine/core'
import {
	IconArrowRight,
	IconArrowUpRight,
	IconChevronLeft,
	IconChevronRight,
	IconPlayerPause,
	IconPlayerPlay,
	IconQuote,
	IconSearch,
} from '@tabler/icons-react'
import Link from 'next/link'
import Head from 'next/head'
import { NextSeo } from 'next-seo'
import NextImage from 'next/image'
import * as React from 'react'

import * as Types from 'declarations'
import { getGuideRoute, getTrekRoute, getUploadThumbPath } from 'lib/display'
import { getDefaultImage } from 'lib/helper'
import * as RedisUtil from 'lib/redis'

import PageTemplate from 'components/PageTemplate'
import { withVersion } from '../lib/withVersion'

type HeroSlide = {
	imageUrl: string
	name: string | null
	href: string | null
	lowestCost: number | null
}

/**
 * Concept 5 "Best of" combines the owner's favourite pieces of concepts 1-4:
 * full-screen rotating photography (1), continent strips with better
 * groupings (2), real rotating reviews (3), and a search-driven finder that
 * fixes the limited table (4). Stats stay present but secondary.
 */

const CDN_HOST = (version: Types.VERSION) =>
	`https://www.guided${
		version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
	}.com`

const cdnImage = (path: string, width: number, version: Types.VERSION) =>
	`${CDN_HOST(version)}/cdn-cgi/image/width=${width},format=auto${path}`

/**
 * Pull the most evocative sentence (the one naming the mountain) out of a
 * raw review and clamp it to `maxWords` words so quotes stay punchy.
 */
const extractQuote = (
	review: Types.App.Types.MountainReview,
	maxWords: number,
): string => {
	const raw = review.reviewText?.trim() ?? ''
	if (!raw) {
		return ''
	}

	const mountainIdx = raw
		.toLowerCase()
		.indexOf(review.mountainName.toLowerCase())

	let snippet = raw
	if (mountainIdx !== -1) {
		const beforeSentences = raw
			.slice(0, mountainIdx)
			.split(/[.!?]+\s/)
			.filter(Boolean)
		const afterText = raw
			.slice(mountainIdx + review.mountainName.length)
			.split(/[.!?]+\s/)[0]

		const before =
			beforeSentences.length > 0
				? beforeSentences[beforeSentences.length - 1]
				: ''
		snippet = `${before} ${review.mountainName} ${afterText ?? ''}`.trim()
	}

	const words = snippet.replace(/\s+/g, ' ').trim().split(' ')
	return words.length > maxWords
		? `${words.slice(0, maxWords).join(' ')}…`
		: snippet
}

/* ------------------------------ 1. Hero --------------------------------- */

const HeroCarousel = ({
	version,
	slides,
	tripCount,
	tripPageCount,
	guideCount,
	countryCount,
	reviewCount,
}: {
	version: Types.VERSION
	slides: HeroSlide[]
	tripCount: number
	tripPageCount: number
	guideCount: number
	countryCount: number
	reviewCount: number
}) => {
	const [activeSlide, setActiveSlide] = React.useState(0)
	const [isPaused, setIsPaused] = React.useState(false)
	const slideCount = slides.length

	// 6s per slide, slow 3s crossfade between stacked photos. Pausing stops
	// the timer; the arrows jump manually in either direction.
	React.useEffect(() => {
		if (isPaused || slideCount < 2) {
			return
		}
		const interval = setInterval(() => {
			setActiveSlide((prev) => (prev + 1) % slideCount)
		}, 6000)
		return () => clearInterval(interval)
	}, [isPaused, slideCount])

	const goNext = () => setActiveSlide((prev) => (prev + 1) % slideCount)
	const goPrev = () =>
		setActiveSlide((prev) => (prev - 1 + slideCount) % slideCount)

	const activeSlideData = slides[activeSlide]
	const fmt = (n: number | undefined) => (n ?? 0).toLocaleString()

	return (
		<section className="c5-hero">
			<Head>
				<link
					rel="preload"
					as="image"
					href={cdnImage(slides[0]?.imageUrl ?? '', 1920, version)}
				/>
			</Head>
			<div className="c5-hero-media">
				{slides.map((slide, index) => (
					<div
						key={slide.imageUrl}
						className={`c5-hero-slide${
							index === activeSlide ? ' c5-hero-slide-active' : ''
						}`}
					>
						<NextImage
							src={cdnImage(slide.imageUrl, 1920, version)}
							alt={slide.name ?? 'Mountain photography'}
							layout="fill"
							objectFit="cover"
							priority={index === 0}
							style={{
								objectFit: 'cover',
								objectPosition: 'center',
							}}
						/>
					</div>
				))}
			</div>
			<Overlay color="#000" opacity={0.5} zIndex={1} />

			<div className="c5-hero-content">
				<h1 className="c5-hero-slogan">
					{version === Types.VERSION.PEAKS
						? 'Find your mountain.'
						: 'Find your trek.'}
				</h1>
				<p className="c5-hero-sub">
					{version === Types.VERSION.PEAKS ? (
						<>
							<strong>{fmt(guideCount)}</strong> guides,{' '}
							<strong>{fmt(tripCount)}</strong> guided expeditions and{' '}
							<strong>{fmt(reviewCount)}</strong> reviews across{' '}
							<strong>{fmt(countryCount)}</strong> countries. Every
							guided climb in one place.
						</>
					) : (
						<>
							<strong>{fmt(guideCount)}</strong> guides,{' '}
							<strong>{fmt(tripCount)}</strong> guided treks and{' '}
							<strong>{fmt(reviewCount)}</strong> reviews across{' '}
							<strong>{fmt(countryCount)}</strong> countries. Every
							guided trek in one place.
						</>
					)}
				</p>
				<div className="c5-hero-ctas">
					<Link href="/expedition-explorer">
						<Button
							size="xl"
							radius="md"
							rightSection={<IconArrowRight size={22} />}
						>
							{fmt(tripPageCount)}{' '}
							{version === Types.VERSION.PEAKS ? 'Mountains' : 'Treks'}
						</Button>
					</Link>
					{version === Types.VERSION.PEAKS && (
						<Link href="/guides">
							<Button
								size="xl"
								radius="md"
								variant="outline"
								className="c5-hero-ghost"
								rightSection={<IconArrowRight size={22} />}
							>
								{fmt(guideCount)} Guides
							</Button>
						</Link>
					)}
				</div>
			</div>

			<div className="c5-hero-foot">
				{activeSlideData?.name && activeSlideData.href && (
					<div className="c5-hero-caption" key={activeSlide}>
						<Text size="xs" c="rgba(255,255,255,0.7)" mb={2}>
							Pictured
						</Text>
						<Link href={activeSlideData.href} className="c5-hero-caption-link">
							{activeSlideData.name}
						</Link>
						{typeof activeSlideData.lowestCost === 'number' && (
							<Text size="xs" c="rgba(255,255,255,0.8)">
								from ${activeSlideData.lowestCost.toLocaleString()}
							</Text>
						)}
					</div>
				)}
				{slideCount > 1 && (
					<div className="c5-hero-controls">
						<ActionIcon
							className="c5-hero-control"
							variant="subtle"
							size="lg"
							onClick={goPrev}
							aria-label="Previous slide"
						>
							<IconChevronLeft size={18} />
						</ActionIcon>
						<ActionIcon
							className="c5-hero-control"
							variant="subtle"
							size="lg"
							onClick={() => setIsPaused((p) => !p)}
							aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
						>
							{isPaused ? (
								<IconPlayerPlay size={18} />
							) : (
								<IconPlayerPause size={18} />
							)}
						</ActionIcon>
						<ActionIcon
							className="c5-hero-control"
							variant="subtle"
							size="lg"
							onClick={goNext}
							aria-label="Next slide"
						>
							<IconChevronRight size={18} />
						</ActionIcon>
					</div>
				)}
			</div>

		</section>
	)
}

/* --------------------------- 2. Review band ------------------------------ */

type BandQuote = {
	text: string
	companyName: string
	companySlug: string
	mountainName: string
	percent: number
}

const FeaturedReview = ({
	reviews,
}: {
	reviews: Types.App.Types.MountainReview[]
}) => {
	const quotes = React.useMemo<BandQuote[]>(
		() =>
			reviews
				.map((review) => ({
					text: extractQuote(review, 15),
					companyName: review.companyName,
					companySlug: review.companySlug,
					mountainName: review.mountainName,
					percent: review.reviewPercent,
				}))
				.filter(({ text }) => text.length > 0)
				.slice(0, 6),
		[reviews],
	)

	const [activeQuote, setActiveQuote] = React.useState(0)

	// Rotate the featured review every 5s with a soft crossfade.
	React.useEffect(() => {
		if (quotes.length < 2) {
			return
		}
		const interval = setInterval(() => {
			setActiveQuote((prev) => (prev + 1) % quotes.length)
		}, 5000)
		return () => clearInterval(interval)
	}, [quotes.length])

	if (quotes.length === 0) {
		return null
	}

	return (
		<div className="c5-featured-quote">
			<div className="c5-quote-stack">
				{quotes.map((quote, index) => (
					<figure
						key={`${quote.companySlug}-${index}`}
						className={index === activeQuote ? 'c5-quote-active' : undefined}
					>
						<blockquote className="c5-band-quote">
							&ldquo;{quote.text}&rdquo;
						</blockquote>
						<figcaption className="c5-band-attr">
							<Link
								href={getGuideRoute({
									slug: quote.companySlug,
								} as Types.API.GuideCompany)}
							>
								{quote.companyName}
							</Link>
							<span aria-hidden="true">&middot;</span>
							<span>{quote.mountainName}</span>
						</figcaption>
						<Box className="c5-band-score">
							<Text span fz="sm" fw={700}>
								★ {quote.percent}%
							</Text>
							<Progress value={quote.percent} size={4} w={110} radius="xl" />
						</Box>
					</figure>
				))}
			</div>
		</div>
	)
}

/* ------------------------ 3. What climbers say --------------------------- */

const ClimberReviews = ({
	reviews,
}: {
	reviews: Types.App.Types.MountainReview[]
}) => {
	const cards = React.useMemo(() => reviews.slice(0, 10), [reviews])

	if (cards.length === 0) {
		return null
	}

	return (
		<section className="c5-reviews">
			<Container size={1100} px="md">
				<Title order={2} ta="center" className="c5-section-title">
					What climbers say
				</Title>
				<Text ta="center" c="dimmed" className="c5-section-sub" mb={24}>
					Aggregated from multiple independent review sites for every
					trip operator and mountain. Each review is matched to the
					climb it describes.
				</Text>
				<FeaturedReview reviews={reviews} />
				<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
					{cards.map((review, index) => (
						<Paper
							key={`${review.companySlug}-${index}`}
							shadow="sm"
							radius="md"
							p="lg"
							withBorder
							className="c5-review-card"
						>
							<Box c="dimmed" mb="xs">
								<IconQuote size={22} stroke={1.5} />
							</Box>
							<Text
								fz="lg"
								className="c5-clamp"
								mb="md"
								style={{ fontStyle: 'italic', lineHeight: 1.45 }}
							>
								&ldquo;{extractQuote(review, 40)}&rdquo;
							</Text>
							<Box className="c5-review-meta">
								<Text span size="sm" fw={600}>
									{review.mountainName}
								</Text>
								<Text span size="sm" c="dimmed">
									&middot;
								</Text>
								<Link
									href={getGuideRoute({
										slug: review.companySlug,
									} as Types.API.GuideCompany)}
									className="c5-review-company"
								>
									{review.companyName}
								</Link>
								<Badge size="xs" variant="light" color="gray">
									{review.provider}
								</Badge>
							</Box>
							<Text span size="sm" fw={700} className="c5-review-score">
								★ {review.reviewPercent}%
							</Text>
						</Paper>
					))}
				</SimpleGrid>
			</Container>
		</section>
	)
}

/* -------------------------- 4. Continent strips -------------------------- */

type ContinentRow = {
	continent: string
	trips: Types.App.HomePageTrip[]
}

/**
 * Flatten every carousel collection (deduped by id), group by continent and
 * sort each group by height. Owner feedback on concept 2: the groupings felt
 * thin, so continents with fewer than two trips merge into one "Other"
 * row at the end instead of leaving lonely single-item strips.
 */
const buildContinentRows = (
	carouselTrips: Types.App.Types.CarouselCollection[],
	otherLabel: string,
): ContinentRow[] => {
	const byContinent = new Map<string, Types.App.HomePageTrip[]>()
	const seen = new Set<number>()

	carouselTrips.forEach(({ trips }) => {
		trips.forEach((trip) => {
			if (seen.has(trip.id)) {
				return
			}
			seen.add(trip.id)
			const continent = trip.continent || 'Worldwide'
			const existing = byContinent.get(continent)
			if (existing) {
				existing.push(trip)
			} else {
				byContinent.set(continent, [trip])
			}
		})
	})

	const byHeightDesc = (a: Types.App.HomePageTrip, b: Types.App.HomePageTrip) =>
		(b.height ?? 0) - (a.height ?? 0)

	const rows = Array.from(byContinent.entries()).map(([continent, trips]) => ({
		continent,
		trips: [...trips].sort(byHeightDesc),
	}))

	const fullRows = rows
		.filter((row) => row.trips.length >= 2)
		.sort((a, b) => b.trips.length - a.trips.length)
	const strays = rows
		.filter((row) => row.trips.length < 2)
		.flatMap((row) => row.trips)
		.sort(byHeightDesc)

	if (strays.length > 0) {
		fullRows.push({ continent: otherLabel, trips: strays })
	}

	return fullRows
}

const ContinentCard = ({ trip }: { trip: Types.App.HomePageTrip }) => (
	<Link
		className="c5-card"
		href={getTrekRoute(trip.trekOrClimb, trip.continent, trip.countries, trip.slug)}
	>
		<span className="c5-card-media">
			<Image
				src={getUploadThumbPath(trip.firstUploadId, 'medium')}
				alt={trip.name}
				fit="cover"
				w="100%"
				h="100%"
			/>
		</span>
		<span className="c5-card-info">
			<span className="c5-card-name">{trip.name}</span>
			<span className="c5-card-meta">
				<span>
					{typeof trip.height === 'number'
						? `${trip.height.toLocaleString()} m`
						: ''}
				</span>
				{typeof trip.lowestCost === 'number' && (
					<span className="c5-card-price">
						from ${trip.lowestCost.toLocaleString()}
					</span>
				)}
			</span>
		</span>
	</Link>
)

const ContinentSections = ({
	version,
	carouselTrips,
}: {
	version: Types.VERSION
	carouselTrips: Types.App.Types.CarouselCollection[]
}) => {
	const rows = React.useMemo(
		() =>
			buildContinentRows(
				carouselTrips,
				version === Types.VERSION.PEAKS ? 'Other mountains' : 'Other treks',
			),
		[carouselTrips, version],
	)

	if (rows.length === 0) {
		return null
	}

	const itemLabel =
		version === Types.VERSION.PEAKS ? 'expeditions' : 'treks'

	return (
		<section className="c5-continents">
			<Title order={2} className="c5-section-title c5-continents-title">
				{version === Types.VERSION.PEAKS
					? 'Mountains by continent'
					: 'Treks by continent'}
			</Title>
			<Text c="dimmed" className="c5-section-sub">
				{version === Types.VERSION.PEAKS
					? 'Where our guided climbs are, tallest first.'
					: 'Where our guided treks are.'}
			</Text>
			{rows.map(({ continent, trips }) => (
				<div className="c5-row" key={continent}>
					<div className="c5-row-head">
						<h2 className="c5-continent-name">{continent}</h2>
						<span className="c5-row-count">
							{trips.length} {itemLabel}
						</span>
					</div>
					<div className="c5-strip">
						{trips.map((trip) => (
							<ContinentCard key={trip.id} trip={trip} />
						))}
					</div>
				</div>
			))}
		</section>
	)
}

/* -------------------------- 5. Expedition finder ------------------------- */

type FinderTrip = {
	id: number
	name: string
	height: number | null
	lowestCost: number | null
	continent: string
	searchText: string
	href: string
}

const ExpeditionFinder = ({
	version,
	finderTrips,
}: {
	version: Types.VERSION
	finderTrips: FinderTrip[]
}) => {
	const [query, setQuery] = React.useState('')
	const [activeContinent, setActiveContinent] = React.useState('All')

	// Full expedition list, prebuilt server-side from the live trip
	// data: every guided trip on the site, not just the carousel lineup.
	const trips = finderTrips

	const continents = React.useMemo(() => {
		const counts = new Map<string, number>()
		trips.forEach((trip) => {
			counts.set(trip.continent, (counts.get(trip.continent) ?? 0) + 1)
		})
		return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
	}, [trips])

	const results = React.useMemo(() => {
		const q = query.trim().toLowerCase()
		return trips.filter(
			(trip) =>
				(activeContinent === 'All' || trip.continent === activeContinent) &&
				(q === '' || trip.searchText.includes(q)),
		)
	}, [trips, query, activeContinent])

	const noun = version === Types.VERSION.PEAKS ? 'expedition' : 'trek'

	return (
		<section className="c5-finder">
			<Container size={1100} px="md">
				<Title order={2} className="c5-section-title">
					All {version === Types.VERSION.PEAKS ? 'expeditions' : 'treks'}
				</Title>
				<Text c="dimmed" className="c5-section-sub" mb="md">
					Every guided {noun} on the site, searchable by name or country.
				</Text>
				<TextInput
					size="xl"
					radius="md"
					placeholder={`Search ${noun}s by name or country`}
					value={query}
					onChange={(event) => setQuery(event.currentTarget.value)}
					leftSection={<IconSearch size={20} />}
					aria-label={`Search ${noun}s`}
				/>

				<div
					className="c5-chip-row"
					role="group"
					aria-label="Filter by continent"
				>
					<UnstyledButton
						type="button"
						className={`c5-chip${
							activeContinent === 'All' ? ' c5-chip-active' : ''
						}`}
						onClick={() => setActiveContinent('All')}
					>
						All <span className="c5-chip-count">{trips.length}</span>
					</UnstyledButton>
					{continents.map(([continent, count]) => (
						<UnstyledButton
							key={continent}
							type="button"
							className={`c5-chip${
								activeContinent === continent ? ' c5-chip-active' : ''
							}`}
							onClick={() => setActiveContinent(continent)}
						>
							{continent} <span className="c5-chip-count">{count}</span>
						</UnstyledButton>
					))}
				</div>

				<Text size="sm" c="dimmed" className="c5-finder-count">
					{results.length.toLocaleString()} of {trips.length.toLocaleString()}{' '}
					{noun}
					{results.length === 1 ? '' : 's'}
				</Text>

				{results.length === 0 ? (
					<Text ta="center" c="dimmed" py="xl" className="c5-finder-empty">
						No matches. Try another name, country, or continent.
					</Text>
				) : (
					<div className="c5-results">
						{results.map((trip) => (
							<Link key={trip.id} href={trip.href} className="c5-result">
								<span className="c5-result-name">{trip.name}</span>
								<span className="c5-result-meta">
									{typeof trip.height === 'number' && (
										<span className="c5-result-height">
											{trip.height.toLocaleString()} m
										</span>
									)}
									{typeof trip.lowestCost === 'number' && (
										<span className="c5-result-price">
											from ${trip.lowestCost.toLocaleString()}
										</span>
									)}
									<IconArrowUpRight size={18} className="c5-result-arrow" />
								</span>
							</Link>
						))}
					</div>
				)}

				<Box ta="center" mt="lg">
					<Link href="/expedition-explorer">
						<Button
							size="md"
							radius="md"
							variant="light"
							rightSection={<IconArrowRight size={18} />}
						>
							Browse all {noun}s
						</Button>
					</Link>
				</Box>
			</Container>
		</section>
	)
}

/* ------------------------- 6. Static world map --------------------------- */

// Pure-SVG equirectangular dot-plot world map (ported from concept 2, no
// new deps). Static by design: dots only, no continuous animation.
const MAP_W = 360 // equirectangular viewBox: 1 unit = 1 degree
const MAP_H = 180

type MapPoly = [number, number][] // [lon, lat] vertices

// Very coarse landmass outlines: only has to read as a world map at dot scale.
const LAND_POLYGONS: MapPoly[] = [
	// North America (mainland + Central America)
	[
		[-168, 68], [-165, 60], [-158, 57], [-152, 59], [-145, 60], [-137, 59],
		[-132, 55], [-125, 49], [-124, 43], [-121, 37], [-117, 33], [-111, 27],
		[-106, 21], [-97, 16], [-92, 14], [-86, 11], [-80, 8], [-78, 9],
		[-83, 11], [-86, 14], [-88, 17], [-87, 21], [-91, 22], [-97, 26],
		[-94, 29], [-89, 29], [-84, 30], [-81, 25], [-80, 27], [-81, 32],
		[-76, 35], [-74, 40], [-70, 42], [-66, 45], [-60, 46], [-53, 49],
		[-56, 52], [-61, 55], [-64, 58], [-69, 59], [-77, 62], [-82, 66],
		[-90, 68], [-100, 69], [-110, 69], [-120, 70], [-130, 69], [-140, 70],
		[-152, 70], [-162, 70],
	],
	// Greenland
	[
		[-45, 60], [-42, 62], [-38, 65], [-32, 68], [-25, 70], [-22, 73],
		[-20, 76], [-25, 79], [-32, 82], [-45, 83], [-55, 82], [-62, 79],
		[-65, 76], [-58, 72], [-53, 67], [-50, 63],
	],
	// South America
	[
		[-78, 8], [-77, 4], [-79, 0], [-81, -4], [-77, -12], [-71, -18],
		[-70, -25], [-71, -32], [-73, -40], [-74, -47], [-72, -53], [-68, -55],
		[-65, -52], [-67, -46], [-65, -41], [-62, -39], [-57, -35], [-53, -32],
		[-48, -27], [-48, -22], [-44, -16], [-39, -10], [-35, -7], [-37, -5],
		[-41, -3], [-47, -1], [-51, 2], [-55, 6], [-60, 9], [-64, 11],
		[-71, 12], [-75, 10],
	],
	// Africa
	[
		[-6, 35], [0, 36], [10, 37], [11, 34], [20, 32], [25, 32], [31, 31],
		[34, 28], [37, 21], [39, 15], [43, 12], [48, 11], [51, 12], [46, 3],
		[42, -1], [40, -7], [37, -13], [35, -19], [35, -24], [31, -29],
		[26, -34], [20, -35], [18, -33], [16, -28], [13, -23], [12, -17],
		[13, -11], [9, -2], [9, 4], [6, 5], [2, 6], [-5, 5], [-8, 4],
		[-13, 8], [-17, 12], [-17, 15], [-16, 20], [-13, 26], [-9, 30],
	],
	// Eurasia (Iberia → Med → Arabia → India → SE Asia → China → Far East →
	// Arctic → Scandinavia → Western Europe)
	[
		[-9, 43], [-9, 38], [-6, 36], [-2, 36], [1, 40], [3, 43], [7, 44],
		[10, 44], [13, 45], [15, 41], [18, 40], [21, 38], [23, 36], [26, 38],
		[26, 40], [29, 41], [31, 36], [36, 36], [35, 33], [34, 30], [35, 28],
		[37, 24], [39, 20], [43, 12], [48, 14], [53, 17], [57, 19], [59, 22],
		[57, 25], [61, 25], [64, 25], [67, 24], [69, 22], [72, 19], [73, 15],
		[76, 9], [78, 8], [80, 10], [80, 14], [83, 17], [86, 20], [89, 22],
		[92, 22], [92, 20], [94, 16], [97, 15], [98, 10], [100, 7], [103, 1],
		[104, 3], [103, 5], [101, 7], [100, 12], [102, 13], [105, 9],
		[107, 11], [109, 13], [109, 16], [107, 18], [106, 20], [108, 21],
		[110, 21], [114, 22], [118, 24], [121, 28], [121, 31], [119, 34],
		[122, 37], [120, 39], [122, 40], [125, 38], [126, 35], [129, 35],
		[129, 38], [128, 40], [131, 43], [135, 44], [138, 47], [141, 52],
		[142, 54], [143, 59], [147, 60], [152, 59], [155, 57], [156, 51],
		[159, 53], [162, 56], [163, 60], [166, 62], [170, 62], [176, 63],
		[180, 65], [186, 66], [188, 67], [180, 69], [170, 70], [160, 71],
		[150, 72], [140, 73], [130, 72], [120, 73], [113, 74], [105, 77],
		[95, 76], [85, 74], [76, 72], [72, 72], [68, 70], [66, 69], [60, 69],
		[55, 68], [44, 67], [40, 66], [36, 65], [33, 67], [30, 70], [25, 71],
		[18, 69], [12, 65], [5, 62], [6, 58], [9, 57], [9, 55], [7, 53],
		[4, 52], [1, 50], [-2, 49], [-2, 47], [-1, 45], [-2, 44],
	],
	// Australia
	[
		[114, -22], [114, -34], [118, -35], [124, -33], [130, -32], [136, -35],
		[140, -38], [146, -39], [150, -37], [153, -32], [153, -27], [150, -22],
		[146, -19], [142, -11], [137, -12], [136, -15], [132, -12], [129, -14],
		[125, -14], [122, -17], [118, -20],
	],
	// Antarctica (band + peninsula toward South America)
	[
		[-180, -70], [180, -70], [180, -75.5], [-180, -75.5],
	],
	[
		[-68, -70], [-65, -63], [-58, -64], [-60, -69],
	],
	// British Isles / Iceland
	[[-5, 50], [1, 51], [0, 53], [-2, 56], [-4, 58], [-5, 57], [-5, 54]],
	[[-10, 52], [-6, 52], [-6, 55], [-8, 55], [-10, 53]],
	[[-24, 64], [-15, 64], [-14, 66], [-20, 66], [-24, 65]],
	// Japan
	[
		[130, 31], [132, 33], [135, 34], [137, 35], [139, 36], [141, 38],
		[141, 41], [140, 43], [143, 44], [145, 44], [145, 42], [142, 40],
		[139, 37], [136, 34], [133, 32],
	],
	// New Zealand
	[[173, -35], [176, -37], [178, -38], [176, -40], [174, -39]],
	[[172, -40], [174, -41], [173, -43], [170, -44], [167, -46], [168, -44], [170, -42]],
	// Madagascar / Sri Lanka / Taiwan / Hainan
	[[43, -13], [50, -15], [50, -16], [48, -24], [45, -25], [43, -22], [44, -17]],
	[[80, 6], [82, 7], [81, 9], [80, 8]],
	[[120, 22], [122, 24], [121, 25], [120, 23]],
	[[108, 18], [111, 19], [110, 20], [108, 19]],
	// Indonesia + Philippines
	[[95, 5], [98, 3], [102, -1], [106, -5], [104, -6], [100, -3], [96, 2]],
	[[105, -6], [114, -8], [114, -9], [105, -7]],
	[[109, 1], [113, 4], [117, 6], [119, 4], [117, 1], [114, -2], [110, -2]],
	[[119, 0], [121, 1], [123, 0], [121, -3], [122, -5], [120, -5], [119, -2]],
	[
		[131, -1], [136, -2], [141, -3], [146, -6], [150, -8], [147, -9],
		[143, -8], [138, -7], [134, -4],
	],
	[[120, 14], [122, 15], [122, 18], [120, 17]],
	[[122, 6], [126, 7], [126, 9], [122, 8]],
	// Cuba / Tasmania / Novaya Zemlya
	[[-85, 22], [-77, 20], [-74, 20], [-78, 23], [-84, 23]],
	[[145, -41], [148, -41], [148, -43], [145, -43]],
	[[52, 71], [55, 73], [57, 76], [54, 76], [52, 73]],
]

// Inland seas carved out of the big Eurasian/North American outlines.
const WATER_POLYGONS: MapPoly[] = [
	// Hudson Bay
	[[-95, 51], [-78, 51], [-77, 63], [-88, 65], [-95, 64]],
	// Baltic
	[[10, 54], [20, 54], [22, 60], [25, 66], [21, 63], [17, 60], [12, 56]],
	// Black Sea
	[[28, 41], [41, 41], [41, 46], [33, 47], [28, 44]],
	// Caspian
	[[47, 37], [54, 37], [54, 47], [49, 47], [47, 42]],
]

const withBounds = (polys: MapPoly[]) =>
	polys.map((poly) => ({
		poly,
		minX: Math.min(...poly.map(([x]) => x)),
		maxX: Math.max(...poly.map(([x]) => x)),
		minY: Math.min(...poly.map(([, y]) => y)),
		maxY: Math.max(...poly.map(([, y]) => y)),
	}))

const LAND_BOUNDS = withBounds(LAND_POLYGONS)
const WATER_BOUNDS = withBounds(WATER_POLYGONS)

const pointInPoly = (lon: number, lat: number, poly: MapPoly) => {
	let inside = false
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const [xi, yi] = poly[i]
		const [xj, yj] = poly[j]
		if (
			yi > lat !== yj > lat &&
			lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
		) {
			inside = !inside
		}
	}
	return inside
}

const isLand = (lon: number, lat: number) => {
	const hitsLand = LAND_BOUNDS.some(
		(b) =>
			lon >= b.minX &&
			lon <= b.maxX &&
			lat >= b.minY &&
			lat <= b.maxY &&
			pointInPoly(lon, lat, b.poly),
	)
	if (!hitsLand) return false
	return !WATER_BOUNDS.some(
		(b) =>
			lon >= b.minX &&
			lon <= b.maxX &&
			lat >= b.minY &&
			lat <= b.maxY &&
			pointInPoly(lon, lat, b.poly),
	)
}

// Built once per process (SSR + client hydrate compute identical output).
const LAND_DOTS: { x: number; y: number }[] = (() => {
	const dots: { x: number; y: number }[] = []
	const STEP = 2.5
	for (let lat = 88.75; lat >= -88.75; lat -= STEP) {
		for (let lon = -178.75; lon <= 178.75; lon += STEP) {
			if (isLand(lon, lat)) {
				dots.push({ x: lon + 180, y: 90 - lat })
			}
		}
	}
	return dots
})()

/* ------------------------------ peak dots ------------------------------ */

const MAP_LOW_RGB = [110, 138, 163] as const // slate blue
const MAP_HIGH_RGB = [237, 104, 65] as const // accent #ed6841

const mixMapColour = (t: number) => {
	const mix = MAP_LOW_RGB.map((c, i) =>
		Math.round(c + (MAP_HIGH_RGB[i] - c) * t),
	)
	return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`
}

type PeakDot = {
	x: number
	y: number
	r: number
	colour: string
	label: string
}

// HomePagePeakLocation carries no name field, so tooltips stay generic
// ("Peak, 6,812 m") instead of inventing hover names.
const buildPeakDots = (
	locations: Types.App.Types.HomePagePeakLocation[],
): PeakDot[] => {
	const heights = locations
		.map((l) => l.height)
		.filter((h): h is number => typeof h === 'number')
	const minH = heights.length ? Math.min(...heights) : 0
	const maxH = heights.length ? Math.max(...heights) : 1
	const range = maxH - minH || 1

	return locations.map(({ latitude, longitude, height }) => {
		const t = typeof height === 'number' ? (height - minH) / range : 0.4
		return {
			x: ((longitude + 180) / 360) * MAP_W,
			y: ((90 - latitude) / 180) * MAP_H,
			r: 1.05 + t * 1.5,
			colour: mixMapColour(t),
			label:
				typeof height === 'number'
					? `Peak, ${height.toLocaleString()} m`
					: 'Peak',
		}
	})
}

const PeakMapSection = ({
	peakLocations,
}: {
	peakLocations: Types.App.Types.HomePagePeakLocation[]
}) => {
	const peakDots = React.useMemo(() => buildPeakDots(peakLocations), [
		peakLocations,
	])

	return (
		<section className="c5-map" aria-label="World map of guided peaks">
			<Container size={1100} px="md">
				<Title order={2} ta="center" className="c5-section-title">
					{peakLocations.length.toLocaleString()} peaks across the world
				</Title>
				<Text ta="center" c="dimmed" className="c5-section-sub" mb={36}>
					Dot size and color follow peak height. Open the map to explore.
				</Text>
				<Link href="/expedition-explorer" className="c5-map-link">
				<svg
					className="c5-worldmap"
					viewBox={`0 0 ${MAP_W} ${MAP_H}`}
					preserveAspectRatio="xMidYMid meet"
					role="img"
					aria-label="World map plotting every guided mountain expedition"
				>
					<g className="c5-map-land">
						{LAND_DOTS.map((dot, i) => (
							<circle key={`land-${i}`} cx={dot.x} cy={dot.y} r={0.72} />
						))}
					</g>
					<g>
						{peakDots.map((dot, i) => (
							<circle
								key={`peak-${i}`}
								className="c5-map-peak"
								cx={dot.x}
								cy={dot.y}
								r={dot.r}
								fill={dot.colour}
							>
								<title>{dot.label}</title>
							</circle>
						))}
					</g>
				</svg>
				</Link>
			</Container>
		</section>
	)
}

/* ------------------------- 7. About note (SEO copy) ---------------------- */

const AboutNote = ({
	version,
	guideCount,
	tripCount,
	countryCount,
	reviewCount,
}: {
	version: Types.VERSION
	guideCount: number
	tripCount: number
	countryCount: number
	reviewCount: number
}) => {
	const fmt = (n: number | undefined) => (n ?? 0).toLocaleString()
	const siteName =
		version === Types.VERSION.PEAKS ? 'Guided Peaks' : 'Guided Trekking'
	const guideLabel =
		version === Types.VERSION.PEAKS ? 'mountain guides' : 'trekking guides'
	const byWord = version === Types.VERSION.PEAKS ? 'mountain' : 'route'
	const climbWord = version === Types.VERSION.PEAKS ? 'climb' : 'trek'

	return (
		<section className="c5-about">
			<Container size={760} px="md">
				<h2 className="c5-about-title">About {siteName}</h2>
				<div className="c5-about-body">
					<p className="c5-about-text">
						{siteName} connects you directly with{' '}
						{version === Types.VERSION.PEAKS ? (
							<Link href="/guides" className="c5-about-link">
								{fmt(guideCount)} {guideLabel}
							</Link>
						) : (
							<>
								{fmt(guideCount)} {guideLabel}
							</>
						)}{' '}
						in {fmt(countryCount)} countries, no booking middlemen. We
						analyze {fmt(reviewCount)} reviews from independent review
						sites and organize them by {byWord}, so you can compare
						guides, prices and seasons across{' '}
						<Link href="/expedition-explorer" className="c5-about-link">
							{fmt(tripCount)}{' '}
							{version === Types.VERSION.PEAKS
								? 'guided expeditions'
								: 'guided treks'}
						</Link>
						.
					</p>
					<p className="c5-about-text">
						For each {byWord}, see what guides offer and what they
						charge. Reviews are pulled from TripAdvisor, Google and
						Trustpilot, and shown against the {byWord} they are about.
					</p>
					<p className="c5-about-text">
						{version === Types.VERSION.PEAKS
							? 'Alps, Andes, Himalaya, Karakoram: from weekend alpine classics to 8,000 m expeditions.'
							: 'Alps, Andes, Atlas, Himalaya: from valley walks to three-week remote treks.'}
					</p>
				</div>
			</Container>
		</section>
	)
}

/* --------------------------- 8. Latest articles -------------------------- */

const LatestArticles = ({ articles }: { articles: Types.API.Article[] }) => {
	const cards = React.useMemo(() => articles.slice(0, 4), [articles])

	if (cards.length === 0) {
		return null
	}

	return (
		<section className="c5-articles">
			<Container size={1100} px="md">
				<div className="c5-articles-head">
					<Title order={2} className="c5-section-title c5-articles-title">
						Latest articles
					</Title>
					<Link href="/articles" className="c5-articles-all">
						View all articles
					</Link>
				</div>
				<SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
					{cards.map((article) => (
						<Link
							key={article.slug}
							href={`/articles/${article.slug}`}
							className="c5-article"
						>
							<span
								className={`c5-article-media${
									article.liveJson.coverImage ? '' : ' c5-article-media-blank'
								}`}
							>
								{article.liveJson.coverImage ? (
									<Image
										src={getUploadThumbPath(
											article.liveJson.coverImage,
											'medium',
										)}
										alt={article.title}
										fit="cover"
										w="100%"
										h="100%"
									/>
								) : null}
							</span>
							<span className="c5-article-title">{article.title}</span>
						</Link>
					))}
				</SimpleGrid>
			</Container>
		</section>
	)
}


/* -------------------------------- the page ------------------------------- */

type HomePageProps = {
	version: Types.VERSION
	guideCount: number
	expedCount: number
	countryCount: number
	reviewCount: number
	guideInfo: {
		logoId: number
		slug: string
		name: string
		isPremium: boolean
		premiumExpiresAt: string | null
	}[]
	premiumGuides: {
		logoId: number
		slug: string
		name: string
		isPremium: boolean
		premiumExpiresAt: string | null
	}[]
	latestArticles: Types.API.Article[]
	carouselTrips: Types.App.Types.CarouselCollection[]
	finderTrips: FinderTrip[]
	continentSummaries: Types.App.Types.ContinentSummary[]
	peakMeta: Types.App.Types.PeakMeta
	trekMeta: Types.App.Types.TrekMeta
	peakLocations: Types.App.Types.HomePagePeakLocation[]
	uniqueGuideLanguages: string[]
	heroSlides: HeroSlide[]
	summitPictures: Types.DB.Models.UploadAttributes[]
	mountainReviews: Types.App.Types.MountainReview[]
}

const HomePage = ({
	version,
	guideCount,
	expedCount,
	countryCount,
	reviewCount,
	finderTrips,
	carouselTrips,
	peakMeta,
	trekMeta,
	heroSlides,
	mountainReviews,
	peakLocations,
	latestArticles,
}: HomePageProps) => {
	const seo = React.useMemo(() => {
		return {
			title:
				version === Types.VERSION.TREKKING
					? 'Find guided treks - Guided Trekking'
					: 'Find Mountaineering Expeditions & Guides - Guided Peaks',
			keywords:
				'mountaineering,mountain climbing,expeditions,guided expeditions,guided climbing,guided mountaineering',
			description:
				version === Types.VERSION.TREKKING
					? 'Curated guide of treks for hikers, walkers, & trekkers. Information for each route to compare & find your best next route.'
					: 'Explore the best mountain climbing & mountaineering expeditions around the world. Join guided climbs, discover mountains, & prepare for your next adventure.',

			canonical: `https://www.guided${
				version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
			}.com`,
			siteName:
				version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
			images: [
				{
					url: getDefaultImage(version),
					type: 'image/jpeg',
				},
			],
			twitter: {
				cardType: 'summary_large_image', // Use 'summary' if no image is available
				site: '@_14335698', // Replace with your Twitter handle
				handle: '@_14335698', // Optional: Handle of the content creator
				image: getDefaultImage(version), // Twitter Card image
			},
		}
	}, [version])

	// expedCount is never returned by getServerSideProps, so the real figure
	// lives in peakMeta. Both are missing on the trekking build.
	const resolvedTripCount =
		version === Types.VERSION.PEAKS
			? peakMeta?.expedCount ?? expedCount ?? 0
			: trekMeta?.tripCount ?? 0


	return (
		<>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
				additionalMetaTags={[
					{
						name: 'keywords',
						content: seo.keywords,
					},
				]}
			/>

			<PageTemplate
				isFullWidth={true}
				isFullHeight={false}
				isWithoutTopMargin={true}
			>
				<div id="homepage">
					<HeroCarousel
						version={version}
						slides={heroSlides}
						tripCount={resolvedTripCount}
						tripPageCount={finderTrips.length}
						guideCount={guideCount}
						countryCount={countryCount}
						reviewCount={reviewCount}
					/>

				<AboutNote
					version={version}
					tripCount={resolvedTripCount}
					guideCount={guideCount}
					countryCount={countryCount}
					reviewCount={reviewCount}
				/>

				<div className="c5-map-finder-row">
					{(peakLocations?.length ?? 0) > 0 && (
						<PeakMapSection peakLocations={peakLocations} />
					)}
					<ExpeditionFinder version={version} finderTrips={finderTrips} />
				</div>

				{version === Types.VERSION.PEAKS && (
					<ClimberReviews reviews={mountainReviews} />
				)}

				<ContinentSections version={version} carouselTrips={carouselTrips} />

				{(latestArticles?.length ?? 0) > 0 && (
					<LatestArticles articles={latestArticles} />
				)}

				</div>
			</PageTemplate>
		</>
	)
}

export const getServerSideProps = withVersion(
	async (context: { version: Types.VERSION }) => {
		const homePageData = await RedisUtil.Data.HomePageData.get({
			version: context.version,
		})

		const {
			trips,
			guideInfo,
			guideCount,
			carouselTrips,
			continentSummaries,
			peakMeta,
			trekMeta,
			peakLocations,
			uniqueGuideLanguages,
			latestArticles,
			reviewCount,
			summitPictures,
			mountainReviews,
			premiumGuides,
		} = homePageData

		const uniqueCountries = [
			...new Set(trips.flatMap((trip) => trip.countries)),
		]

		// Curated hero slides. Peaks images are named by trip slug; the slug
		// is resolved against the live trip list below, so captions (name,
		// page link, from-price) always match current data. A slide without a
		// slug (or a slug that no longer resolves) renders image-only — and
		// logs server-side so a broken entry gets noticed.
		const heroSlideDefs: { image: string; slug?: string }[] =
			context.version === Types.VERSION.PEAKS
				? [
						{ image: '405', slug: 'ama-dablam' }, // Ama Dablam, camp 3
						{ image: '903', slug: 'baruntse' },
						{ image: '1202', slug: 'alpamayo' },
						{ image: '1203', slug: 'alpamayo' },
						{ image: '1322', slug: 'korzhenevskaya' },
						{ image: '1600', slug: 'chogolisa' },
						{ image: '1835', slug: 'illampu' },
						{ image: '1839', slug: 'illampu' },
				  ]
				: ['1', '59', '69', '730', '732', '1118', '1142', '1146', '1469'].map(
						(file) => ({ image: file }), // trekking: not yet slug-mapped
				  )

		const tripsBySlug = new Map(trips.map((trip) => [trip.slug, trip]))

		// Slim projection of every guided trip (the full directory, not just
		// the curated carousel lineup) for the homepage finder.
		const allTrips = await RedisUtil.Data.ExplorePageData.get({
			version: context.version,
		})
		const finderTrips: FinderTrip[] = allTrips.map((trip) => ({
			id: trip.id,
			name: trip.name,
			height: trip.height ?? null,
			lowestCost: trip.lowestCost ?? null,
			continent: trip.continent || 'Worldwide',
			searchText: `${trip.name} ${trip.countries.join(' ')}`.toLowerCase(),
			href: getTrekRoute(
				trip.trekOrClimb,
				trip.continent,
				trip.countries,
				trip.slug,
			),
		}))

		const heroSlides: HeroSlide[] = heroSlideDefs.map(({ image, slug }) => {
			const trip = slug ? tripsBySlug.get(slug) : undefined
			if (slug && !trip) {
				console.error(
					`Homepage hero: no trip found for slug "${slug}" — showing image without caption`,
				)
			}
			return {
				imageUrl: `/static/homepage-carousel/${
					context.version === Types.VERSION.PEAKS ? 'peaks' : 'trekking'
				}/${image}.jpg`,
				name: trip?.name ?? null,
				href: trip
					? getTrekRoute(
							trip.trekOrClimb,
							trip.continent,
							trip.countries,
							trip.slug,
					  )
					: null,
				lowestCost: trip?.lowestCost ?? null,
			}
		})

		return {
			props: {
				guideCount,
				countryCount: uniqueCountries.length,
				guideInfo,
				version: context?.version,
				latestArticles,
				carouselTrips,
				finderTrips,
				continentSummaries,
				peakMeta,
				trekMeta,
				peakLocations,
				uniqueGuideLanguages,
				heroSlides,
				// base,
				summitPictures,
				reviewCount,
				mountainReviews,
				premiumGuides,
			},
		}
	},
)

export default HomePage
